import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { useEffect } from 'react'

import { useAnalyticsConsent } from '@analytics/consent'

import { DefaultConfiguration, ampli, type IdentifyProperties } from '../../ampli'
import { useDepositJourneyAutoAbandon } from './deposit'
import { useWithdrawalJourneyAutoAbandon } from './withdrawal'

export { getAmplitudeIdentifyProperties } from './identifyProperties'

const GLOBAL_DONE = '__AMPLITUDE_INIT_DONE__'
const GLOBAL_PROMISE = '__AMPLITUDE_INIT_PROMISE__'
const GLOBAL_CLIENT = '__AMPLITUDE_CLIENT__'
const GLOBAL_SR_PLUGIN = '__AMPLITUDE_SR_PLUGIN__'

function getApiKey() {
  return import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined
}

export function getAmplitudeClient(): amplitude.Types.BrowserClient | undefined {
  if (typeof window === 'undefined') return undefined
  return (window as any)[GLOBAL_CLIENT] as amplitude.Types.BrowserClient | undefined
}

export function useAmplitude() {
  const hasConsent = useAnalyticsConsent()

  useEffect(() => {
    if (hasConsent) {
      initAmplitudeClientOnce()
      getAmplitudeClient()?.setOptOut(false)
      return
    }

    // Consent withdrawn (or not yet given) after the client was created:
    // stop collecting, keep the instance so a later grant can resume.
    getAmplitudeClient()?.setOptOut(true)
  }, [hasConsent])
}

export function initAmplitudeClientOnce() {
  if (typeof window === 'undefined') return

  const enabled = String(import.meta.env.VITE_AMPLITUDE_ENABLED ?? 'false') === 'true'
  if (!enabled) return

  if ((window as any)[GLOBAL_DONE]) return
  if ((window as any)[GLOBAL_PROMISE]) return
  ;(window as any)[GLOBAL_PROMISE] = (async () => {
    try {
      const apiKey = getApiKey()
      if (!apiKey) {
        throw new Error('[Amplitude] Missing VITE_AMPLITUDE_API_KEY')
      }

      const environment = (import.meta.env.VITE_ENV ?? 'dev') as string
      const platform = (import.meta.env.VITE_PLATFORM ?? 'web_client_area') as string

      const client = amplitude.createInstance()
      ;(window as any)[GLOBAL_CLIENT] = client

      const envPlatformPlugin = {
        name: 'env-platform-plugin',
        type: 'enrichment',
        setup() {},
        execute(event: any) {
          event.event_properties = {
            ...(event.event_properties ?? {}),
            environment,
            platform,
          }
          return event
        },
      }

      await client.add(envPlatformPlugin as any).promise

      await client.init(apiKey, undefined, {
        ...DefaultConfiguration,
        autocapture: false,
      }).promise

      const sampleRateRaw =
        (import.meta.env.VITE_AMPLITUDE_SR_SAMPLE_RATE as string | undefined) ??
        (import.meta.env.PROD ? '0' : '1')

      const sampleRate = Number(sampleRateRaw)

      if (Number.isFinite(sampleRate) && sampleRate > 0) {
        const sr = sessionReplayPlugin({
          sampleRate,
          debugMode: false,
          privacyConfig: {
            maskSelector: ['input', 'textarea'],
            blockSelector: 'iframe, .amp-block, [data-amp-block], [data-3ds], [data-psp]',
          },
        })

        ;(window as any)[GLOBAL_SR_PLUGIN] = sr
        await client.add(sr as any).promise
      }

      ampli.load({ client: { instance: client } })
      ;(window as any)[GLOBAL_DONE] = true
    } catch (err) {
      delete (window as any)[GLOBAL_PROMISE]
      delete (window as any)[GLOBAL_CLIENT]
      delete (window as any)[GLOBAL_SR_PLUGIN]
      console.error('[Amplitude] init failed', err)
    }
  })()
}

export function identifyAmplitudeUser(userId: string, properties?: IdentifyProperties) {
  if (typeof window === 'undefined') return
  if (!userId) return

  const client = getAmplitudeClient()
  if (!client) return

  client.setUserId(userId)

  if (properties && ampli.isLoaded) {
    ampli.identify(userId, properties)
  }
}

export function resetAmplitudeUser() {
  if (typeof window === 'undefined') return

  const client = getAmplitudeClient()
  if (!client) return

  client.reset()
}

export const useAmplitudeJournies = () => {
  useDepositJourneyAutoAbandon()
  useWithdrawalJourneyAutoAbandon()
}
